import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Paper, Grid, Card, CardContent, Typography, Chip, Button,
  TextField, MenuItem, CircularProgress, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ApiService from '../../functions/apiService';
import AuthContext from '../../context/AuthContext';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const SWAP_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'normal', label: 'Normal' },
  { value: 'collateral', label: 'Collateral' },
];

const statusChipSx = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'accepted' || s === 'approved') {
    return { backgroundColor: 'rgba(1,120,79,0.1)', color: 'rgba(1,120,79,1)', border: '1px solid rgba(1,120,79,0.4)' };
  }
  if (s === 'rejected' || s === 'denied') {
    return { backgroundColor: 'rgba(230,39,25,0.1)', color: '#e62719', border: '1px solid rgba(230,39,25,0.4)' };
  }
  return { backgroundColor: 'rgba(255,165,0,0.16)', color: '#FF8C00', border: '1px solid rgba(255,165,0,0.28)' };
};

const empLabel = (obj, prefix) => {
  if (!obj) return '';
  return (
    obj[`${prefix}employee`]?.fullName ||
    obj[`${prefix}Employee`]?.fullName ||
    obj[`${prefix}Emp_Name`] ||
    obj[`${prefix}Employee_Name`] ||
    obj[`${prefix}Emp_Code`] ||
    ''
  );
};

const fmtDate = (d) => (d ? moment(d).format('MM/DD/YYYY') : '—');
const fmtTime = (t) => (t ? String(t).slice(0, 5) : '—');

const ManageSwapRequests = () => {
  const { permission, v3Data } = useContext(AuthContext);
  const allowed = permission?.includes('Emp_Auth') && v3Data?.V3_SHIFT_TRADE_ENABLED == 'true';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState('');
  const [swapType, setSwapType] = useState('');

  // Approve / Deny modal
  const [actionModal, setActionModal] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  const [actionStatus, setActionStatus] = useState('accepted');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = () => {
    setLoading(true);
    const body = {};
    if (fromDate) body.from_date = moment(fromDate).format('YYYY-MM-DD');
    if (toDate) body.to_date = moment(toDate).format('YYYY-MM-DD');
    if (status) body.Status = status;
    if (swapType) body.Swap_Type = swapType;
    ApiService.post('/volunteer-shift-swap-requests', body)
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.data || data?.requests || []);
        setRequests(list || []);
      })
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const openAction = (row, newStatus) => {
    setActionRow(row);
    setActionStatus(newStatus);
    setReason('');
    setActionModal(true);
  };

  const submitAction = () => {
    const id = actionRow?.ID ?? actionRow?.Id ?? actionRow?.id;
    if (!id) {
      toast.error('Unable to identify this request');
      return;
    }
    setSubmitting(true);
    const body = { ID: id, Status: actionStatus, Reason: reason };
    ApiService.post('/update-volunteer-shift-swap-request-status', body)
      .then((res) => {
        toast.success(res?.message || 'Request updated successfully');
        setActionModal(false);
        fetchRequests();
      })
      .catch((err) => {
        toast.error(err?.data?.message || err?.response?.data?.message || 'Failed to update request');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Box sx={{ m: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SwapHorizIcon sx={{ color: '#BF1D2D' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'karla, sans-serif' }}>
          Manage Swap Requests
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>From Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={fromDate}
                onChange={(v) => setFromDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14 } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>To Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={toDate}
                onChange={(v) => setToDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14 } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status</Typography>
            <TextField select size="small" fullWidth value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Swap Type</Typography>
            <TextField select size="small" fullWidth value={swapType} onChange={(e) => setSwapType(e.target.value)}>
              {SWAP_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" fullWidth onClick={fetchRequests}
                sx={{ backgroundColor: '#BF1D2D', textTransform: 'none', fontFamily: 'karla, sans-serif', boxShadow: 'none', '&:hover': { backgroundColor: '#a5182a', boxShadow: 'none' } }}>
                Filter
              </Button>
              <Button variant="outlined" onClick={() => { setFromDate(null); setToDate(null); setStatus(''); setSwapType(''); setTimeout(fetchRequests, 0); }}
                sx={{ textTransform: 'none', fontFamily: 'karla, sans-serif', color: '#BF1D2D', borderColor: '#BF1D2D', '&:hover': { borderColor: '#a5182a' } }}>
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
      ) : requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', pt: 6, color: 'text.secondary' }}>
          <Typography>No swap requests found.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {requests.map((req, i) => {
            const reqId = req?.ID ?? req?.Id ?? req?.id ?? i;
            const isCollateral = String(req?.Swap_Type || '').toLowerCase() === 'collateral';
            const isPending = String(req?.Status || '').toLowerCase() === 'pending';
            return (
              <Grid item xs={12} md={6} lg={4} key={reqId}>
                <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #EAECF0', boxShadow: 'none' }}>
                  <CardContent sx={{ flex: 1 }}>
                    {/* Top: type + status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Chip size="small" label={isCollateral ? 'Collateral Swap' : 'Normal Swap'}
                        sx={{ fontWeight: 600, backgroundColor: 'rgba(191,29,45,0.08)', color: '#BF1D2D', border: '1px solid rgba(191,29,45,0.25)' }} />
                      <Chip size="small" label={req?.Status || '—'} sx={{ fontWeight: 600, textTransform: 'capitalize', ...statusChipSx(req?.Status) }} />
                    </Box>

                    {/* From -> To */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Requested By</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{empLabel(req, '') || req?.Emp_Code || '—'}</Typography>
                      </Box>
                      <ArrowRightAltIcon sx={{ color: '#BF1D2D' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Swap With</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{empLabel(req, 'New_') || empLabel(req, 'new_') || req?.New_Emp_Code || '—'}</Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Shift details */}
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Shift Date</Typography>
                        <Typography variant="body2">{fmtDate(req?.Shift_Date)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Shift / Time</Typography>
                        <Typography variant="body2">
                          {req?.Shift_Code != null ? `#${req.Shift_Code} · ` : ''}{fmtTime(req?.Start_Time)} - {fmtTime(req?.End_Time)}
                        </Typography>
                      </Grid>
                      {req?.Position_Name && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Position</Typography>
                          <Typography variant="body2">{req.Position_Name}</Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Collateral details */}
                    {isCollateral && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ color: '#BF1D2D', fontWeight: 700 }}>Collateral Shift</Typography>
                        <Grid container spacing={1} sx={{ mt: 0 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Date</Typography>
                            <Typography variant="body2">{fmtDate(req?.Collateral_Shift_Date)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Shift / Time</Typography>
                            <Typography variant="body2">
                              {req?.Collateral_Shift_Code != null ? `#${req.Collateral_Shift_Code} · ` : ''}{fmtTime(req?.Collateral_Start_Time)} - {fmtTime(req?.Collateral_End_Time)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {req?.Reason && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Reason</Typography>
                        <Typography variant="body2">{req.Reason}</Typography>
                      </Box>
                    )}
                  </CardContent>

                  {/* Actions */}
                  {isPending && (
                    <Box sx={{ display: 'flex', gap: 1, p: 1.5, pt: 0 }}>
                      <Button fullWidth variant="contained" onClick={() => openAction(req, 'accepted')}
                        sx={{ backgroundColor: '#01784F', textTransform: 'none', boxShadow: 'none', '&:hover': { backgroundColor: '#015c3c', boxShadow: 'none' } }}>
                        Approve
                      </Button>
                      <Button fullWidth variant="outlined" onClick={() => openAction(req, 'rejected')}
                        sx={{ textTransform: 'none', color: '#BF1D2D', borderColor: '#BF1D2D', '&:hover': { borderColor: '#a5182a', backgroundColor: 'rgba(191,29,45,0.04)' } }}>
                        Deny
                      </Button>
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Approve / Deny modal */}
      <Dialog open={actionModal} onClose={() => !submitting && setActionModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'karla, sans-serif' }}>
          {actionStatus === 'accepted' ? 'Approve Swap Request' : 'Deny Swap Request'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {actionStatus === 'accepted'
              ? 'Approve this shift swap request?'
              : 'Deny this shift swap request?'}
          </Typography>
          <TextField
            label="Reason"
            placeholder="Enter a reason (optional)"
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionModal(false)} disabled={submitting}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitAction} disabled={submitting}
            sx={{
              textTransform: 'none', boxShadow: 'none',
              backgroundColor: actionStatus === 'accepted' ? '#01784F' : '#BF1D2D',
              '&:hover': { boxShadow: 'none', backgroundColor: actionStatus === 'accepted' ? '#015c3c' : '#a5182a' },
            }}>
            {submitting ? 'Saving…' : (actionStatus === 'accepted' ? 'Approve' : 'Deny')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSwapRequests;
